import { maybe, rd, RemoteData } from '@passionware/monads';
import { SimpleReadOnlyEvent } from '@passionware/simple-event';
import { QueryClient, useQueries, useQuery } from '@tanstack/react-query';
import { throttle } from 'lodash';
import zipObject from 'lodash/zipObject';
import type { ResourcingBillingTree } from 'v5/core/production-resourcing/billing-tree';
import {
  createProductionResourcingApi,
  primeLegacyCachesFromV1
} from 'v5/api/production-resourcing/productionResourcing.api.http';
import { V1Action } from 'v4/migration/actionAsEventMiddleware';
import { Batcher, createBatcher } from '../../../../v4/entities/common/batcher';
import type { BookingApi } from 'v5/api/booking/booking.api';
import {
  ProductionApi,
  ProductionBatchIdsQuery,
  ProductionListResponse
} from 'v5/api/production/production.api';
import { WithWorkspace } from '../../../api/_common/query/workspace';
import { createStrongCache } from '../../../platform/lang/strongCache';
import { normalizeQuery } from '../../../api/_common/query/queryUtils';
import { Production } from '../../../../__types__';
import { ensureIdleQuery } from '../../../platform/ensureIdleQuery';
import { parseEntityId } from 'v5/platform/lang/entityId';
import { WithServices } from '../../../platform/ts/services';
import { WithErrorService } from '../../front/ErrorService/ErrorService';
import { WithMessageService } from '../../internal/MessageService/MessageService';
import { ProductionService } from './ProductionService';

export function createProductionService(
  config: WithServices<[WithMessageService, WithErrorService]> & {
    api: ProductionApi;
    bookingApi: BookingApi;
    queryClient: QueryClient;
    v1Event: SimpleReadOnlyEvent<V1Action>;
  }
): ProductionService {
  const { api, bookingApi, queryClient, v1Event } = config;
  const {
    services: { messageService, errorService }
  } = config;
  const interceptor = errorService.withCategory('ProductionService');

  const invalidateAll = throttle(
    async () => {
      await queryClient.cancelQueries({
        predicate: query =>
          Array.isArray(query.queryKey) &&
          query.queryKey.includes('resourcing-bundle')
      });
      queryClient.removeQueries({
        queryKey: ['production'],
        type: 'inactive'
      });
      await queryClient.invalidateQueries({
        queryKey: ['production']
      });
      await queryClient.refetchQueries({
        queryKey: ['production'],
        type: 'active'
      });
    },
    // we don't want to invalidate the cache too often
    3000,
    {
      // after period of inactivity, we want to invalidate the cache immediately
      leading: true,
      // we want to make sure that last invalidation is executed
      trailing: true
    }
  );

  /**
   * Extracts production IDs from a booking's resource_slot_assignments.
   * Returns unique production IDs that are affected by this booking.
   */
  const extractProductionIdsFromBooking = (
    booking: any
  ): Production['id'][] => {
    if (!booking?.resource_slot_assignments) {
      return [];
    }

    const productionIds = new Set<Production['id']>();
    for (const assignment of booking.resource_slot_assignments) {
      const productionId = assignment?.resource_slot?.production_id;
      if (maybe.isPresent(productionId)) {
        productionIds.add(productionId);
      }
    }
    return Array.from(productionIds);
  };

  const productionDetailQueryKey = (id: Production['id']) =>
    ['production', 'detail', id] as const;

  const ensureProduction = (id: unknown) => {
    const productionId = parseEntityId(id);
    if (productionId === null) {
      return Promise.reject(new Error('Production ID is required'));
    }
    return queryClient.fetchQuery({
      queryKey: productionDetailQueryKey(productionId),
      queryFn: interceptor.wrap(() => api.getProduction(productionId))
    });
  };

  const productionResourcingApi = createProductionResourcingApi({
    bookingApi,
    ensureProduction
  });

  /** Nested under detail so `invalidateQueries({ queryKey: productionDetail })` also drops resourcing. */
  const productionResourcingBundleQueryKey = (productionId: Production['id']) =>
    [...productionDetailQueryKey(productionId), 'resourcing-bundle'] as const;

  const productionResourcingBundlesBatchQueryKey = (
    workspace: string,
    productionIds: Production['id'][]
  ) =>
    [
      'production',
      'resourcing-bundle',
      'batch',
      workspace,
      [...productionIds].sort((a, b) => a - b)
    ] as const;

  /**
   * Resourcing bundle batch keys are not under a single production id — invalidate
   * any active batch query that includes one of the affected ids.
   */
  const invalidateResourcingBundlesForProductionIds = async (
    productionIds: Production['id'][]
  ) => {
    if (productionIds.length === 0) {
      return;
    }

    for (const id of productionIds) {
      queryClient.removeQueries({
        queryKey: productionResourcingBundleQueryKey(id),
        type: 'inactive'
      });
    }

    await Promise.all(
      productionIds.map(id =>
        queryClient.invalidateQueries({
          queryKey: productionResourcingBundleQueryKey(id)
        })
      )
    );

    await queryClient.invalidateQueries({
      predicate: query => {
        const key = query.queryKey;
        if (!Array.isArray(key)) {
          return false;
        }
        const batchIndex = key.indexOf('batch');
        if (batchIndex < 0 || key[batchIndex - 1] !== 'resourcing-bundle') {
          return false;
        }
        const idsInBatch = key[key.length - 1];
        if (!Array.isArray(idsInBatch)) {
          return false;
        }
        return productionIds.some(id => idsInBatch.includes(id));
      }
    });
  };

  /**
   * Invalidates specific production caches by their IDs.
   * More efficient than invalidating all productions.
   */
  const invalidateProductionsById = async (
    productionIds: Production['id'][]
  ) => {
    if (productionIds.length === 0) {
      return;
    }

    // Remove inactive queries for these productions
    for (const id of productionIds) {
      queryClient.removeQueries({
        queryKey: productionDetailQueryKey(id),
        type: 'inactive'
      });
    }

    // Invalidate active queries for these productions (detail + nested resourcing-bundle)
    await Promise.all(
      productionIds.map(id =>
        queryClient.invalidateQueries({
          queryKey: productionDetailQueryKey(id)
        })
      )
    );

    await invalidateResourcingBundlesForProductionIds(productionIds);

    // Also invalidate list queries since they might include these productions
    queryClient.removeQueries({
      queryKey: ['production', 'list'],
      type: 'inactive'
    });
    await queryClient.invalidateQueries({
      queryKey: ['production', 'list'],
      type: 'active'
    });
  };

  v1Event.addListener(async event => {
    switch (event.type) {
      case 'CREATE_PRODUCTION_SUCCESS':
      case 'UPDATE_PRODUCTION_SUCCESS':
      case 'DELETE_PRODUCTION_SUCCESS':
      case 'UNARCHIVE_PRODUCTION_SUCCESS':
        await invalidateAll();
        return;
      case 'CREATE_BOOKING_SUCCESS': {
        // New bookings will always have resource_slot_assignments if they're assigned to productions.
        // We can use selective invalidation here since we know which productions are affected.
        const booking = event.result;
        if (booking) {
          const affectedProductionIds =
            extractProductionIdsFromBooking(booking);
          if (affectedProductionIds.length > 0) {
            await invalidateProductionsById(affectedProductionIds);
          }
        }
        return;
      }
      case 'UPDATE_BOOKING_SUCCESS': {
        // IMPORTANT: When a booking is updated, we MUST invalidate ALL production caches
        // because assignments may have been removed, and we can't detect which productions
        // were affected without tracking "before state".
        //
        // Problem scenarios:
        // 1. Booking had assignments to Production A and Production B
        //    - User unassigns from Production A
        //    - Booking now only has assignment to Production B
        //    - We can't detect that Production A was affected (it's no longer in the booking)
        //
        // 2. Booking had assignment to Production A
        //    - User unassigns completely
        //    - Booking now has no assignments
        //    - We can't detect that Production A was affected
        //
        // 3. Booking had no assignments
        //    - User assigns to Production A
        //    - We CAN detect Production A (it's in the booking)
        //    - But we can't distinguish this from scenario 2 without "before state"
        //
        // Solution: Always invalidate all productions for UPDATE_BOOKING_SUCCESS.
        // This ensures correctness, even though it's less efficient.
        //
        // Performance note: This could be triggered frequently via websocket updates.
        // However, invalidating production queries is relatively cheap since:
        // 1. TanStack Query uses stale-while-revalidate (5min staleTime)
        // 2. Only active queries are refetched immediately
        // 3. Inactive queries are just marked stale and refetch on next use
        // 4. Most booking updates don't affect productions (bookings without assignments)
        //
        // If this becomes a performance issue, consider:
        // - Adding assignmentsRemoved to V1Action type to track unassignments
        // - Using a more sophisticated cache invalidation strategy
        // - Tracking "before state" in the event system
        await invalidateAll();
        return;
      }
      case 'DELETE_RESOURCE_SLOT_ASSIGNMENT_SUCCESS': {
        // When a resource slot assignment is deleted, we know which production is affected
        // because the action includes production_id. We can use selective invalidation.
        const action = event as any; // V1Action type doesn't include payload, but runtime does
        if (maybe.isPresent(action.production_id)) {
          await invalidateProductionsById([action.production_id]);
        } else {
          // Fallback: if production_id is missing, invalidate all to be safe
          await invalidateAll();
        }
        return;
      }
      case 'UPDATE_RESOURCE_SLOT': {
        // When a resource slot is updated, it may affect the production's resource_slots.
        // The action includes production_id, so we can use selective invalidation.
        // Note: This is the UPDATE action (not SUCCESS), but it's what's available in V1Action.
        const action = event as any; // V1Action type doesn't include payload, but runtime does
        if (maybe.isPresent(action.production_id)) {
          await invalidateProductionsById([action.production_id]);
        } else {
          // Fallback: if production_id is missing, invalidate all to be safe
          await invalidateAll();
        }
        return;
      }
      case 'DELETE_BOOKING_SUCCESS':
      case 'CREATE_EXPENSE_SUCCESS':
      case 'UPDATE_EXPENSE_SUCCESS':
      case 'DELETE_EXPENSE_SUCCESS':
      case 'CREATE_EXPENSE_RECEIPT_SUCCESS':
      case 'UPDATE_EXPENSE_RECEIPT_SUCCESS':
      case 'DELETE_EXPENSE_RECEIPT_SUCCESS':
      case 'CREATE_INVOICES_SUCCESS':
      case 'UPDATE_INVOICE_SUCCESS':
      case 'DELETE_INVOICE_SUCCESS':
      case 'BOOKING_CREATED':
      case 'BOOKING_UPDATED':
        await invalidateAll();
        return;
    }
  });

  messageService.reportSystemEffect.subscribeToRequest(async message => {
    for (const effect of message.request.scope) {
      switch (effect.entity) {
        case 'production': {
          // first, we invalidate all list queries
          queryClient.removeQueries({
            queryKey: ['production', 'list'],
            type: 'inactive'
          });
          await queryClient.invalidateQueries({
            queryKey: ['production', 'list'],
            type: 'active'
          });
          // then we invalidate the detail query
          // Detail key must match useProduction(id) → ['production', 'detail', id]. Resourcing mutations await this invalidation (via awaitReportSystemEffects) before marking success.
          await queryClient.invalidateQueries({
            queryKey: ['production', 'detail', effect.entityId]
          });
          await queryClient.refetchQueries({
            queryKey: ['production', 'detail', effect.entityId],
            type: 'active'
          });
          await queryClient.invalidateQueries({
            queryKey: ['production', 'associations', effect.entityId]
          });
          break;
        }
        case 'booking':
        case 'expense':
          await invalidateAll();
          break;
      }
    }
    message.sendResponse();
  });

  // Lazy factory for batchers per workspace
  // Ensures requests from different workspaces are not batched together
  const batcherFactory = createStrongCache<
    string,
    Batcher<Production['id'], ProductionListResponse, Production | null>
  >(
    workspace =>
      createBatcher(
        async ids => {
          if (ids.length === 0) {
            return { items: [], count: 0 };
          }
          const batchQuery: WithWorkspace<ProductionBatchIdsQuery> = {
            workspace,
            filters: {
              id: {
                operator: 'matchAny' as const,
                value: ids
              }
            }
          };
          return await interceptor.wrap(() => api.getProductions(batchQuery))();
        },
        (response, id) => {
          for (const row of response.items) {
            if (row.isValid && row.data.id === id) {
              return row.data;
            }
          }
          return null;
        }
      ),
    workspace => maybe.of(workspace)
  );

  const resourcingBatcherFactory = createStrongCache<
    string,
    Batcher<
      Production['id'],
      Record<Production['id'], ResourcingBillingTree>,
      ResourcingBillingTree
    >
  >(
    workspace =>
      createBatcher(
        async (ids, signal) => {
          if (ids.length === 0) {
            return {};
          }
          const { trees, primes } = await interceptor.wrap(() =>
            productionResourcingApi.getBillingTreesByIds(ids)
          )();
          for (const [productionId, prime] of Object.entries(primes)) {
            primeLegacyCachesFromV1(
              queryClient,
              productionDetailQueryKey,
              prime
            );
          }
          return trees;
        },
        (trees, id) => {
          const tree = trees[id];
          if (!tree) {
            throw new Error(`Production resourcing tree missing for id ${id}`);
          }
          return tree;
        }
      ),
    workspace => maybe.of(workspace)
  );

  const service: ProductionService = {
    useProductions: query =>
      ensureIdleQuery(
        query,
        useQuery(
          {
            enabled: maybe.isPresent(query),
            queryKey: ['production', 'list', maybe.map(query, normalizeQuery)],
            queryFn: interceptor.wrap(async () => {
              const result = await maybe.map(query, api.getProductions);
              // Populate detail cache with productions from list response
              if (maybe.isPresent(result)) {
                for (const row of result.items) {
                  if (row.isValid) {
                    queryClient.setQueryData(
                      ['production', 'detail', row.data.id],
                      row.data
                    );
                  }
                }
              }
              return result;
            })
          },
          queryClient
        )
      ),
    useProductionsById: (workspace, ids) => {
      // Always call useQueries (hooks must be called unconditionally)
      const batcher = batcherFactory.getOrCreate(workspace);

      // Use useQueries with individual production queries (same as useProduction)
      const results = useQueries(
        {
          queries: ids.map(id => ({
            queryKey: ['production', 'detail', id],
            queryFn: async ({ signal }: { signal: AbortSignal }) => {
              const batched = await batcher.fetch({
                payload: id,
                signal
              });
              if (!batched) {
                throw new Error(`Production with id ${id} not found`);
              }
              return batched;
            },
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 10 * 60 * 1000 // 10 minutes
          }))
        },
        queryClient
      );

      // Convert UseQueryResult to Record<id, RemoteData<Production>>
      // UseQueryResult is directly assignable to RemoteData
      return zipObject(ids, results);
    },
    ensureProductions: query =>
      queryClient.fetchQuery({
        queryKey: ['production', 'list', query],
        queryFn: interceptor.wrap(() => api.getProductions(query))
      }),
    useProduction: (id, workspace) =>
      ensureIdleQuery(
        id,
        useQuery(
          {
            enabled: maybe.isPresent(id),
            queryKey: ['production', 'detail', id],
            queryFn: async ({ signal }: { signal: AbortSignal }) => {
              if (!id) {
                throw new Error('Production ID is required');
              }

              const batcher = batcherFactory.getOrCreate(workspace);
              const batched = await batcher.fetch({
                payload: id,
                signal
              });
              if (!batched) {
                throw new Error(`Production with id ${id} not found`);
              }
              return batched;
            }
          },
          queryClient
        )
      ),
    ensureProduction,

    useProductionResourcingTree: (id, workspace) => {
      const batcher = resourcingBatcherFactory.getOrCreate(workspace);
      return ensureIdleQuery(
        id,
        useQuery(
          {
            enabled: maybe.isPresent(id) && workspace !== '',
            queryKey: maybe.isPresent(id)
              ? productionResourcingBundleQueryKey(maybe.getOrThrow(id))
              : ['production', 'detail', 'idle', 'resourcing-tree'],
            queryFn: async ({ signal }: { signal: AbortSignal }) => {
              const productionId = maybe.getOrThrow(id);
              return batcher.fetch({ payload: productionId, signal });
            },
            staleTime: 5 * 60 * 1000,
            gcTime: 10 * 60 * 1000
          },
          queryClient
        )
      );
    },

    useProductionResourcingTreesById: (workspace, ids) => {
      const batcher = resourcingBatcherFactory.getOrCreate(workspace);
      const results = useQueries(
        {
          queries: ids.map(id => ({
            queryKey: productionResourcingBundleQueryKey(id),
            queryFn: async ({ signal }: { signal: AbortSignal }) => {
              const tree = await batcher.fetch({ payload: id, signal });
              if (!tree) {
                throw new Error(
                  `Production resourcing tree missing for id ${id}`
                );
              }
              return tree;
            },
            staleTime: 5 * 60 * 1000,
            gcTime: 10 * 60 * 1000
          }))
        },
        queryClient
      );
      return zipObject(ids, results);
    },

    ensureProductionResourcingTree: async id => {
      const tree = await queryClient.fetchQuery({
        queryKey: productionResourcingBundleQueryKey(id),
        queryFn: interceptor.wrap(async () => {
          const { trees, primes } =
            await productionResourcingApi.getBillingTreesByIds([id]);
          const resolved = trees[id];
          if (!resolved) {
            throw new Error(`Production resourcing tree missing for id ${id}`);
          }
          const prime = primes[id];
          if (prime) {
            primeLegacyCachesFromV1(
              queryClient,
              productionDetailQueryKey,
              prime
            );
          }
          return resolved;
        })
      });
      return tree;
    }
  };

  return service;
}
